/**
 * Build Your Own Box — cart grouping (Option A).
 * Groups line items that share properties._box_id and syncs one quantity control
 * across every member of the group via /cart/change.js.
 */
(function () {
  const GROUP_ATTR = 'data-byob-box-id';

  function moneyFormat(cents) {
    const value = (Number(cents) || 0) / 100;
    try {
      const currency = window.Shopify?.currency?.active || 'INR';
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    } catch (e) {
      return `₹${value}`;
    }
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  class ByobCartGroups {
    constructor(container) {
      this.container = container;
      this.init();
    }

    init() {
      this.groupRows();
      this.bindGroupControls();
    }

    findRows() {
      return [...this.container.querySelectorAll(`tr[${GROUP_ATTR}], .cart-items__table-row[${GROUP_ATTR}]`)];
    }

    groupRows() {
      const rows = this.findRows();
      if (!rows.length) return;

      const groups = new Map();
      rows.forEach((row) => {
        const id = row.getAttribute(GROUP_ATTR);
        if (!id) return;
        if (!groups.has(id)) groups.set(id, []);
        groups.get(id).push(row);
      });

      groups.forEach((groupRows, boxId) => {
        if (groupRows.length < 1) return;
        if (groupRows[0].closest('[data-byob-box-group]')) return;

        const name = groupRows[0].getAttribute('data-byob-box-name') || 'Gift Box';
        const qty = Number(groupRows[0].getAttribute('data-byob-box-qty')) || 1;
        let lineTotal = 0;
        groupRows.forEach((row) => {
          lineTotal += Number(row.getAttribute('data-byob-line-price')) || 0;
        });

        const wrapper = document.createElement('tbody');
        wrapper.className = 'byob-cart-group';
        wrapper.setAttribute('data-byob-box-group', boxId);

        const header = document.createElement('tr');
        header.className = 'byob-cart-group__header cart-items__table-row';
        const safeId = String(boxId).replace(/[^a-zA-Z0-9_-]/g, '');
        header.innerHTML = `
          <td colspan="4" class="byob-cart-group__header-cell">
            <div class="byob-cart-group__bar">
              <button type="button" class="byob-cart-group__toggle" aria-expanded="true" data-byob-box-toggle>
                <span class="byob-cart-group__chevron" aria-hidden="true">▾</span>
                <span class="byob-cart-group__title">${escapeHtml(name)}</span>
                <span class="byob-cart-group__count">${groupRows.length} items</span>
              </button>
              <div class="byob-cart-group__qty">
                <label class="visually-hidden" for="byob-qty-${safeId}">Box quantity</label>
                <button type="button" class="byob-cart-group__qty-btn" data-byob-box-qty-change="-1" aria-label="Decrease box quantity">−</button>
                <input id="byob-qty-${safeId}" class="byob-cart-group__qty-input" type="number" min="0" max="99" value="${qty}" data-byob-box-qty-input>
                <button type="button" class="byob-cart-group__qty-btn" data-byob-box-qty-change="1" aria-label="Increase box quantity">+</button>
              </div>
              <div class="byob-cart-group__total" data-byob-box-total>${moneyFormat(lineTotal)}</div>
              <button type="button" class="byob-cart-group__remove" data-byob-box-remove aria-label="Remove gift box">Remove</button>
            </div>
          </td>
        `;

        const parent = groupRows[0].parentElement;
        parent.insertBefore(wrapper, groupRows[0]);
        wrapper.appendChild(header);

        groupRows.forEach((row) => {
          row.classList.add('byob-cart-group__item');
          // Hide per-line quantity controls — group control owns quantity
          const qtyCell = row.querySelector('.cart-items__quantity');
          if (qtyCell) qtyCell.classList.add('byob-cart-group__qty-hidden');
          wrapper.appendChild(row);
        });
      });
    }

    bindGroupControls() {
      this.container.querySelectorAll('[data-byob-box-group]').forEach((group) => {
        if (group.dataset.byobBound) return;
        group.dataset.byobBound = 'true';

        group.querySelector('[data-byob-box-toggle]')?.addEventListener('click', () => {
          const collapsed = group.classList.toggle('is-collapsed');
          const btn = group.querySelector('[data-byob-box-toggle]');
          if (btn) btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        });

        group.querySelectorAll('[data-byob-box-qty-change]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const input = group.querySelector('[data-byob-box-qty-input]');
            if (!input) return;
            const delta = Number(btn.getAttribute('data-byob-box-qty-change')) || 0;
            const next = Math.max(0, Number(input.value) + delta);
            input.value = String(next);
            this.updateGroupQuantity(group, next);
          });
        });

        group.querySelector('[data-byob-box-qty-input]')?.addEventListener('change', (e) => {
          const next = Math.max(0, Math.floor(Number(e.target.value) || 0));
          e.target.value = String(next);
          this.updateGroupQuantity(group, next);
        });

        group.querySelector('[data-byob-box-remove]')?.addEventListener('click', () => {
          this.updateGroupQuantity(group, 0);
        });
      });
    }

    async updateGroupQuantity(group, quantity) {
      const rows = [...group.querySelectorAll(`[${GROUP_ATTR}]`)];
      const updates = rows
        .map((row) => {
          const key = row.getAttribute('data-key') || row.dataset.key;
          return key ? { id: key, quantity } : null;
        })
        .filter(Boolean);

      if (!updates.length) return;

      group.classList.add('is-updating');

      try {
        // Sequential change.js keeps line keys stable across updates
        for (const update of updates) {
          const res = await fetch(`${cartRoot()}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(update),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Could not update box quantity');
          }
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              data: { source: 'byob-cart-group' },
            },
          })
        );
      } catch (error) {
        console.error(error);
        alert(error.message || 'Could not update gift box quantity');
        group.classList.remove('is-updating');
      }
    }
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function boot() {
    document.querySelectorAll('.cart-items, cart-items-component, .cart-drawer__items').forEach((el) => {
      if (el.dataset.byobCartReady) return;
      el.dataset.byobCartReady = 'true';
      new ByobCartGroups(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cart:update', () => {
    // Re-run after theme morphs cart HTML
    setTimeout(() => {
      document.querySelectorAll('[data-byob-cart-ready]').forEach((el) => {
        delete el.dataset.byobCartReady;
      });
      // also clear the flag we set
      document.querySelectorAll('.cart-items, cart-items-component, .cart-drawer__items').forEach((el) => {
        delete el.dataset.byobCartReady;
      });
      boot();
    }, 350);
  });
})();
